import { useState, useEffect, useCallback, useRef } from 'react';

interface WebSpeechRecordingOptions {
  language?: string;
  continuous?: boolean;
  interimResults?: boolean;
  maxAlternatives?: number;
}

interface WebSpeechRecordingState {
  isRecording: boolean;
  transcript: string;
  interimTranscript: string;
  error: string | null;
  isSupported: boolean;
  confidence: number;
}

export const useWebSpeechRecording = (options: WebSpeechRecordingOptions = {}) => {
  const [state, setState] = useState<WebSpeechRecordingState>({
    isRecording: false,
    transcript: '',
    interimTranscript: '',
    error: null,
    isSupported: false,
    confidence: 0
  });

  const recognitionRef = useRef<InstanceType<typeof window.SpeechRecognition> | null>(null);
  const restartTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRecordingRef = useRef<boolean>(false);

  // Check if Web Speech API is supported
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const isSupported = !!SpeechRecognition;
    
    setState(prev => ({ ...prev, isSupported }));

    if (isSupported) {
      recognitionRef.current = new SpeechRecognition();
      const recognition = recognitionRef.current;

      // Configure recognition
      recognition.continuous = options.continuous ?? true;
      recognition.interimResults = options.interimResults ?? true;
      recognition.lang = options.language || 'zh-CN';
      recognition.maxAlternatives = options.maxAlternatives || 1;

      // Event handlers
      recognition.onstart = () => {
        isRecordingRef.current = true;
        setState(prev => ({ ...prev, isRecording: true, error: null }));
      };

      recognition.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';
        let confidence = 0;

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            finalTranscript += result[0].transcript;
            confidence = result[0].confidence;
          } else {
            interimTranscript += result[0].transcript;
          }
        }

        setState(prev => ({
          ...prev,
          transcript: prev.transcript + finalTranscript,
          interimTranscript,
          confidence
        }));
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        isRecordingRef.current = false;
        setState(prev => ({ 
          ...prev, 
          error: event.error,
          isRecording: false 
        }));
      };

      recognition.onend = () => {
        const wasRecording = isRecordingRef.current;
        setState(prev => ({ ...prev, isRecording: false }));
        isRecordingRef.current = false;
        
        // Auto-restart for continuous recording (handle 60-second limit)
        if (options.continuous && wasRecording) {
          restartTimeoutRef.current = setTimeout(() => {
            if (recognitionRef.current) {
              try {
                recognitionRef.current.start();
                isRecordingRef.current = true;
              } catch (e) {
                console.warn('Failed to restart speech recognition:', e);
              }
            }
          }, 100);
        }
      };
    }

    return () => {
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
    };
  }, [options.continuous, options.interimResults, options.language, options.maxAlternatives]);

  const startRecording = useCallback(() => {
    if (!state.isSupported) {
      setState(prev => ({ ...prev, error: 'Web Speech API is not supported in this browser' }));
      return;
    }

    if (recognitionRef.current && !state.isRecording) {
      setState(prev => ({ ...prev, transcript: '', interimTranscript: '', error: null }));
      recognitionRef.current.start();
    }
  }, [state.isSupported, state.isRecording]);

  const stopRecording = useCallback(() => {
    isRecordingRef.current = false;
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
    }
  }, []);

  const resetTranscript = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      transcript: '', 
      interimTranscript: '',
      confidence: 0
    }));
  }, []);

  return {
    ...state,
    startRecording,
    stopRecording,
    resetTranscript
  };
};

// Type declarations are in src/types/speech-recognition.d.ts